import React from "react";
import moment from "moment-timezone";
import { PrioritySpan, priorityClasses } from "./styles";

// Set default controls so that the datetime picker is populated on
// first render. The uuids are random (required by query controls).
export const DEFAULT_CONTROLS = {
  "371ff3e6-9f20-4d09-b9ba-63c2de96c824": {
    field: "created",
    value: ["30d"],
    operator: "in",
    uuid: "371ff3e6-9f20-4d09-b9ba-63c2de96c824",
  },
};

export const Priority = (props) => {
  return (
    <PrioritySpan>
      <div className={priorityClasses.container}>
        <div
          className={priorityClasses.circle}
          style={{ backgroundColor: props.color }}
        />
        P{props.priority}
      </div>
    </PrioritySpan>
  );
};

export const CONSTANTS = {
  jurisdiction: {
    10: "Administrator",
    20: "Manager",
    30: "Analyst",
    40: "Responder",
  },
  status: {
    0: "Open",
    10: "Analysis in Progress",
    20: "Analysis Complete",
    30: "Response in Progress",
    40: "Resolved",
  },
  type: {
    10: "Operational",
    15: "Risk",
    20: "Suspect",
    30: "Threat",
    40: "System",
  },
  category: {
    0: "Unknown",
    10: "Unauthorized Access Attempt",
    20: "Malicious Code",
    30: "Vulnerability Exploit Attempt",
    40: "Reconnaissance",
    50: "Denial of Service",
    60: "Policy Violation",
    70: "Assets",
    80: "Authentication",
    90: "Code Injection",
    100: "Collection",
    110: "Command and Control",
    120: "Command Line Logging",
    125: "Credential Access",
    130: "Defense Evasion",
    140: "Discovery/Reconnaissance",
    150: "EventIDs",
    160: "Execution",
    170: "Exfiltration",
    175: "Impact",
    180: "Initial Access",
    190: "Internal",
    200: "Lateral Movement",
    210: "Persistance",
    220: "Privilege Escalation",
    225: "resource Development",
    230: "Threats",
    240: "Users and Groups",
  },
  resolution: {
    10: "Valid",
    20: "False Positive",
    30: "No Action Needed",
    40: "Risk Accepted",
  },
  priority: {
    1: <Priority color="red" priority="1" />,
    2: <Priority color="#FDE03A" priority="2" />,
    3: <Priority color="gray" priority="3" />,
  },
  blocked: {
    false: "Not Blocked",
    true: "Blocked",
  },
};

export const AVAILABLE_COLUMNS = [
  {
    field: "status",
    flex: 0.3,
    title: "Status",
    valueGetter: ({ value }) => CONSTANTS.status[value],
  },
  {
    title: "Created",
    field: "created",
    flex: 0.4,
    type: "string",
    valueGetter: ({ value }) => {
      if (!value) return "";
      return moment
        .utc(value)
        .tz(moment.tz.guess(true))
        .format("MMM D, YYYY h:mm A z");
    },
  },
  {
    title: "Short ID",
    field: "shortId",
    flex: 0.4,
  },
  {
    title: "Type",
    field: "type",
    flex: 0.3,
    valueGetter: ({ value }) => CONSTANTS.type[value],
  },
  {
    title: "Priority",
    field: "priority",
    flex: 0.3,
    renderCell: (params) => CONSTANTS.priority[params.value],
  },
  {
    title: "Name",
    field: "name",
    flex: 0.5,
  },
  {
    title: "Analysis",
    field: "analysis",
    flex: 1.25,
    valueGetter: ({ value }) => {
      // Truncate analyses that are too long
      if (value && value.length > 400) {
        const words = value.slice(0, 400).split(" ");
        return words.slice(0, words.length - 1).join(" ") + " …";
      }
      return value;
    },
  },
  {
    title: "Category",
    flex: 1,
    field: "category",
    valueGetter: ({ value }) => CONSTANTS.category[value],
  },
  {
    title: "Blocked",
    flex: 0.5,
    field: "blocked",
    type: "boolean",
    valueGetter: ({ value }) => CONSTANTS.blocked[value],
  },
  {
    title: "Assignees",
    flex: 0.5,
    field: "ownersExpanded",
    sortable: false,
    valueGetter: (params) => {
      const owners = params.row.ownersExpanded;
      if (owners) {
        return owners.map((o) => o.email).join(", ");
      }
      // TODO `owners_expanded` isn't working properly when is distinct
      // is checked so for now just return null but we should fix this.
      return null;
    },
  },
];

// Default columns for the finding page, in order of columns in the table
export const DEFAULT_COLUMN_FIELDS = [
  "status",
  "created",
  "shortId",
  "type",
  "priority",
  "name",
  "analysis",
  "ownersExpanded",
];

export const getColumns = (fields) => {
  return AVAILABLE_COLUMNS.filter((c) => fields.includes(c.field));
};

export const RESOLUTION_OPTIONS = Object.entries(CONSTANTS.resolution).map(
  ([k, v]) => ({
    label: v,
    value: k,
  })
);

export const getFindingTableQuickFilters = ({
  findings = [],
  isTableFilteredByType = false,
}) => {
  // we are only concerned with showing "Open" findings in the quick filters
  const openFindings = findings.filter(({ status }) => status === 0);
  const totalCount = openFindings.length;
  const totalOperationalCount = openFindings.filter(
    ({ type }) => type === 10
  ).length;
  const totalRiskCount = openFindings.filter(({ type }) => type === 15).length;
  const totalSuspectCount = openFindings.filter(
    ({ type }) => type === 20
  ).length;
  const totalThreatCount = openFindings.filter(
    ({ type }) => type === 30
  ).length;

  return [
    {
      label: isTableFilteredByType ? "All" : `All (${totalCount})`,
      queryParameter: null,
    },
    {
      label: isTableFilteredByType
        ? "Operational"
        : `Operational (${totalOperationalCount})`,
      queryParameter: { field: "type", value: 10, operator: "eq" },
    },
    {
      label: isTableFilteredByType ? "Risk" : `Risk (${totalRiskCount})`,
      queryParameter: { field: "type", value: 15, operator: "eq" },
    },
    {
      label: isTableFilteredByType
        ? "Suspect"
        : `Suspect (${totalSuspectCount})`,
      queryParameter: { field: "type", value: 20, operator: "eq" },
    },
    {
      label: isTableFilteredByType ? "Threat" : `Threat (${totalThreatCount})`,
      queryParameter: { field: "type", value: 30, operator: "eq" },
    },
  ];
};

export const getQueryParameterFromFilter = ({ filterLabel, findings }) => {
  let queryParameterToReturn = null;

  if (!!findings && !!filterLabel) {
    const tableFilterOptions = getFindingTableQuickFilters({ findings });

    const activeFilter = tableFilterOptions.find(
      // this looks this way since the labels are dynamic and display a count at certain
      // times, so we are only concerned with the first word i.e. 'Suspect' or 'Threat'
      ({ label }) => label.includes(filterLabel.split("")[0])
    );

    if (activeFilter) queryParameterToReturn = activeFilter.queryParameter;
  }

  return queryParameterToReturn;
};

// given the table quick filter options from above find if one of the quick filters
// is present as a query param in the UI's current queryControls and if so return
// that label so we can display it as active to the user
export const getSelectedFilterFromQueryControls = (queryControls = {}) => {
  // default to all
  let selectedFilterToReturn = "All";

  const tableFilterOptions = getFindingTableQuickFilters({ findings: [] });

  // find if one of the quick filters (outside of 'All') is selected
  const activeTableFilter = tableFilterOptions.find((filterOption) => {
    return !!Object.keys(queryControls).find((qcUUID) => {
      return (
        queryControls[qcUUID] &&
        filterOption.queryParameter &&
        queryControls[qcUUID].field === "type" &&
        queryControls[qcUUID].value === filterOption.queryParameter.value
      );
    });
  });

  if (activeTableFilter) selectedFilterToReturn = activeTableFilter.label;

  return selectedFilterToReturn;
};
